import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export type FeatureFlag = keyof typeof environment.featureFlags;

const STORAGE_KEY = 'taptimize_user_view';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  /**
   * Check whether a feature flag is enabled.
   *
   * - In production: always returns the environment default (no overrides).
   * - In dev/staging: returns the environment default unless "User View"
   *   is active, in which case all flags return false (simulates production).
   */
  isEnabled(flag: FeatureFlag): boolean {
    if (environment.production) {
      return environment.featureFlags[flag];
    }

    // Non-production: respect "User View" toggle
    if (this.isUserView()) {
      return false; // simulate production — all experimental flags off
    }

    return environment.featureFlags[flag];
  }

  /**
   * Toggle between Dev View and User View.
   * Only meaningful in non-production environments.
   */
  toggleUserView(): void {
    if (environment.production) {
      return; // no-op in production
    }

    const current = this.isUserView();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(!current));
  }

  /**
   * Whether the developer is currently in "User View" mode
   * (simulating production with all feature flags off).
   */
  isUserView(): boolean {
    if (environment.production) {
      return false;
    }

    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'false');
    } catch {
      return false;
    }
  }

  /**
   * Whether the current environment is production.
   * Useful for templates that need to conditionally render dev-only UI.
   */
  isProduction(): boolean {
    return environment.production;
  }
}

